import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import HumanizerAddress from '@common/components/HumanizerAddress'
import NetworkIcon from '@common/components/NetworkIcon'
import Text from '@common/components/Text'
import TokenOrNft from '@common/components/TokenOrNft'
import useTheme from '@common/hooks/useTheme'
import { RADII } from '@common/modules/dashboard/home-kit/tokens'
import flexbox from '@common/styles/utils/flexbox'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'

interface Props {
  // The CCTPIntent typed-message body: sourceChainId / amount / destChainId /
  // recipient / targetToken. Loosely typed because it comes off the raw message.
  message: Record<string, any>
  accent: string
}

// Clean design clause for a LibertySwap CCTP cross-chain intent (`CCTPIntent`),
// which the humanizer can't translate (no token-approval semantics) — so we read
// the typed-message fields directly and render a "Cross-chain transfer" card
// instead of the raw key/value fallback. All semantic theme tokens.
const Row: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <View
    style={[flexbox.directionRow, flexbox.alignCenter, flexbox.justifySpaceBetween, { gap: 12 }]}
  >
    <Text fontSize={13} appearance="secondaryText">
      {label}
    </Text>
    {children}
  </View>
)

const ChainPill: FC<{ chainId: bigint; name: string }> = ({ chainId, name }) => (
  <View style={[flexbox.directionRow, flexbox.alignCenter, { gap: 6 }]}>
    <NetworkIcon id={chainId.toString()} size={18} />
    <Text fontSize={13} weight="medium" numberOfLines={1}>
      {name}
    </Text>
  </View>
)

const CrossChainClause: FC<Props> = ({ message, accent }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { networks } = useNetworksControllerState()

  const data = useMemo(() => {
    if (!message) return null
    const { sourceChainId, destChainId, amount, recipient, targetToken } = message
    if (sourceChainId === undefined || destChainId === undefined || amount === undefined) return null
    const nameOf = (id: any) =>
      networks.find((n) => n.chainId === BigInt(id))?.name || `Chain ${id}`
    return {
      sourceChainId: BigInt(sourceChainId),
      destChainId: BigInt(destChainId),
      amount: BigInt(amount),
      recipient: recipient as string | undefined,
      targetToken: targetToken as string | undefined,
      sourceName: nameOf(sourceChainId),
      destName: nameOf(destChainId)
    }
  }, [message, networks])

  if (!data) return null

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: `${accent}47`,
        borderRadius: RADII.md,
        backgroundColor: theme.controlSurfaceBackground as string,
        padding: 16,
        gap: 14
      }}
    >
      <View style={[flexbox.directionRow, flexbox.alignCenter, flexbox.justifySpaceBetween]}>
        <Text fontSize={14} weight="semiBold" color={accent}>
          {t('Cross-chain transfer')}
        </Text>
        <View
          style={{
            paddingVertical: 3,
            paddingHorizontal: 9,
            borderRadius: 100,
            backgroundColor: theme.quinaryBackground as string,
            borderWidth: 1,
            borderColor: theme.secondaryBorder as string
          }}
        >
          <Text fontSize={11} weight="medium" appearance="secondaryText">
            {t('1 of 1')}
          </Text>
        </View>
      </View>

      {!!data.targetToken && (
        <TokenOrNft
          value={data.amount}
          address={data.targetToken}
          chainId={data.destChainId}
          textSize={24}
          hideLinks
        />
      )}

      <View
        style={{
          gap: 8,
          paddingTop: 12,
          borderTopWidth: 1,
          borderTopColor: theme.primaryBorder as string
        }}
      >
        <Row label={t('From')}>
          <ChainPill chainId={data.sourceChainId} name={data.sourceName} />
        </Row>
        <Row label={t('To')}>
          <ChainPill chainId={data.destChainId} name={data.destName} />
        </Row>
        {!!data.recipient && (
          <Row label={t('Recipient')}>
            <HumanizerAddress fontSize={13} address={data.recipient} chainId={data.destChainId} />
          </Row>
        )}
      </View>
    </View>
  )
}

export default React.memo(CrossChainClause)
